// T070: Create image upload service for steemitimages.com

import { Buffer } from 'buffer'
import { PrivateKey } from '@steempro/dsteem'

const STEEMIT_IMAGES_API = 'https://steemitimages.com'
const IMAGE_SIGNING_CHALLENGE = 'ImageSigningChallenge'

export interface UploadImageResult {
  success: boolean
  url?: string
  error?: string
}

export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
}

export interface UploadImageParams {
  file: File
  username: string
  postingKey: string
  onProgress?: (progress: UploadProgress) => void
}

/**
 * Creates a signature for image upload
 * The signature is created by signing sha256('ImageSigningChallenge' + image_data) with posting key
 * Uses Web Crypto API for SHA256 hash calculation
 */
async function createImageSignature(
  imageBuffer: ArrayBuffer,
  postingKey: string
): Promise<string> {
  // Convert ArrayBuffer to Uint8Array for processing
  const imageData = new Uint8Array(imageBuffer)

  // Create the message to hash: 'ImageSigningChallenge' + image_data
  const encoder = new TextEncoder()
  const challengeBytes = encoder.encode(IMAGE_SIGNING_CHALLENGE)

  // Combine challenge and image data
  const combined = new Uint8Array(challengeBytes.length + imageData.length)
  combined.set(challengeBytes, 0)
  combined.set(imageData, challengeBytes.length)

  // Calculate SHA256 hash using Web Crypto API
  const hashBuffer = await crypto.subtle.digest('SHA-256', combined)
  const hashArray = new Uint8Array(hashBuffer)

  // Convert to Buffer-like format for dsteem signing
  // dsteem expects Buffer, but we can pass Uint8Array which works similarly
  const privateKey = PrivateKey.fromString(postingKey)
  const signature = privateKey.sign(Buffer.from(hashArray))

  return signature.toString()
}

/**
 * Uploads an image to steemitimages.com
 * @param params Upload parameters including file, username, and postingKey
 * @returns Upload result with URL or error
 */
export async function uploadImage(params: UploadImageParams): Promise<UploadImageResult> {
  const { file, username, postingKey, onProgress } = params

  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      return {
        success: false,
        error: 'File must be an image',
      }
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'Image size must be less than 10MB',
      }
    }

    // Read file as ArrayBuffer for signing
    const imageBuffer = await file.arrayBuffer()

    // Create signature
    const signature = await createImageSignature(imageBuffer, postingKey)

    // Create form data
    const formData = new FormData()
    formData.append('file', file)

    // Upload with progress tracking
    const xhr = new XMLHttpRequest()

    const uploadPromise = new Promise<UploadImageResult>((resolve) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100),
          })
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText)
            if (response.url) {
              resolve({
                success: true,
                url: response.url,
              })
            } else {
              resolve({
                success: false,
                error: 'No URL in response',
              })
            }
          } catch {
            resolve({
              success: false,
              error: 'Invalid response format',
            })
          }
        } else {
          // Parse error response
          let errorMessage = `Upload failed with status ${xhr.status}`
          try {
            const errorResponse = JSON.parse(xhr.responseText)
            if (errorResponse.error?.name) {
              errorMessage = errorResponse.error.name
            }
          } catch {
            // Use default error message
          }
          resolve({
            success: false,
            error: errorMessage,
          })
        }
      })

      xhr.addEventListener('error', () => {
        resolve({
          success: false,
          error: 'Network error during upload',
        })
      })

      xhr.addEventListener('abort', () => {
        resolve({
          success: false,
          error: 'Upload cancelled',
        })
      })

      // Upload to /<username>/<signature> endpoint
      xhr.open('POST', `${STEEMIT_IMAGES_API}/${username}/${signature}`)
      xhr.send(formData)
    })

    return await uploadPromise
  } catch (error) {
    console.error('Error uploading image:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

export interface UploadImagesParams {
  files: File[]
  username: string
  postingKey: string
  onProgress?: (fileIndex: number, progress: UploadProgress) => void
}

/**
 * Uploads multiple images
 * @param params Upload parameters including files, username, and postingKey
 * @returns Array of upload results
 */
export async function uploadImages(params: UploadImagesParams): Promise<UploadImageResult[]> {
  const { files, username, postingKey, onProgress } = params
  const results: UploadImageResult[] = []

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    if (!file) continue

    const result = await uploadImage({
      file,
      username,
      postingKey,
      onProgress: (progress) => {
        onProgress?.(i, progress)
      },
    })
    results.push(result)
  }

  return results
}

/**
 * Validates an image file before upload
 * @param file The file to validate
 * @returns Validation result
 */
export function validateImageFile(file: File): {
  valid: boolean
  error?: string
} {
  // Check if it's an image
  if (!file.type.startsWith('image/')) {
    return {
      valid: false,
      error: 'File must be an image',
    }
  }

  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'Image size must be less than 10MB',
    }
  }

  // Check supported formats
  const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  if (!supportedFormats.includes(file.type)) {
    return {
      valid: false,
      error: 'Unsupported image format. Use JPEG, PNG, GIF, or WebP',
    }
  }

  return { valid: true }
}

/**
 * Extracts images from markdown text
 * @param markdown The markdown text
 * @returns Array of image URLs
 */
export function extractImagesFromMarkdown(markdown: string): string[] {
  const imageRegex = /!\[.*?\]\((https?:\/\/[^\s)]+)\)/g
  const matches = markdown.matchAll(imageRegex)
  const urls: string[] = []

  for (const match of matches) {
    if (match[1]) {
      urls.push(match[1])
    }
  }

  return urls
}
